import { useMemo, forwardRef } from "react";
import SectionNavigationContext, {
  SectionNavigationContextComponent,
  useSectionNavigationContext,
} from "./section-navigation-context";
import {
  SectionNavigationComposition,
  SectionNavigationProps,
} from "./interfaces";
import { SectionNavigationRef } from "./types";
import useSectionNavigationUtils from "./utils/use-section-navigation-utils";
import SectionNavigationNavWrapper from "./section-navigation-nav-wrapper";
import SectionNavigationHeader from "./section-navigation-header";
import SectionNavigationList from "./section-navigation-list";
import SectionNavigationListItem from "./section-navigation-list-item";
import SectionNavigationListItemContent from "./section-navigation-list-item-content";
import SectionNavigationToggle from "./section-navigation-toggle";
import SectionNavigationAuxiliaryWrapper from "./section-navigation-auxiliary-wrapper/section-navigation-auxiliary-wrapper";
import SectionNavigationNavListBuilder from "./section-navigation-nav-list-builder";

import "./section-navigation.scss";

const SectionNavigation = forwardRef<
  SectionNavigationRef,
  SectionNavigationProps
>(function SectionNavigation(
  {
    customHTMLTag: Wrapper = "div",
    toggleExpandedLabel = "Collapse",
    toggleCollapsedLabel = "Expand",
    currentItemId,
    isFlat,
    className: consumerBlockClassName,
    uniqueName,
    children,
  },
  forwardedRef,
) {
  const controlledProps = useMemo(
    () => ({
      toggleExpandedLabel,
      toggleCollapsedLabel,
      currentItemId,
      isFlat,
      consumerBlockClassName,
      uniqueName,
    }),
    [
      consumerBlockClassName,
      currentItemId,
      isFlat,
      toggleCollapsedLabel,
      toggleExpandedLabel,
      uniqueName,
    ],
  );

  const utils = useSectionNavigationUtils(controlledProps);

  return (
    <SectionNavigationContext.Provider value={utils}>
      <Wrapper ref={forwardedRef} className={utils.getBlocksWith()}>
        {children}
      </Wrapper>
    </SectionNavigationContext.Provider>
  );
}) as SectionNavigationComposition<
  SectionNavigationRef,
  SectionNavigationProps
>;

SectionNavigationContextComponent.displayName = "SectionNavigation.Context";
SectionNavigation.Context = SectionNavigationContextComponent;

SectionNavigationHeader.displayName = "SectionNavigation.Header";
SectionNavigation.Header = SectionNavigationHeader;

SectionNavigationToggle.displayName = "SectionNavigation.Toggle";
SectionNavigation.Toggle = SectionNavigationToggle;

SectionNavigationNavWrapper.displayName = "SectionNavigation.NavWrapper";
SectionNavigation.NavWrapper = SectionNavigationNavWrapper;

SectionNavigationAuxiliaryWrapper.displayName =
  "SectionNavigation.AuxiliaryWrapper";
SectionNavigation.AuxiliaryWrapper = SectionNavigationAuxiliaryWrapper;

SectionNavigationList.displayName = "SectionNavigation.List";
SectionNavigation.List = SectionNavigationList;

SectionNavigationListItem.displayName = "SectionNavigation.ListItem";
SectionNavigation.ListItem = SectionNavigationListItem;

SectionNavigationListItemContent.displayName =
  "SectionNavigation.ListItemContent";
SectionNavigation.ListItemContent = SectionNavigationListItemContent;

SectionNavigationNavListBuilder.displayName =
  "SectionNavigation.NavListBuilder";
SectionNavigation.NavListBuilder = SectionNavigationNavListBuilder;

SectionNavigation.useSectionNavigationContext = useSectionNavigationContext;

export default SectionNavigation;

export * from "./interfaces";
export * from "./types";
export * from "./section-navigation-context/interfaces";
export * from "./section-navigation-list/interfaces";
export * from "./section-navigation-list-item/interfaces";
export * from "./section-navigation-nav-wrapper/interfaces";
export * from "./section-navigation-header/interfaces";
export * from "./section-navigation-auxiliary-wrapper/interfaces";
export * from "./section-navigation-nav-list-builder/interfaces";
